import { get as getLegacy, del as delLegacy } from 'idb-keyval';
import type { StateStorage } from 'zustand/middleware';
import { createLogger } from '../logger';

const log = createLogger('session-storage');

/**
 * Session-scoped storage engine for Zustand.
 * Keeps state only for the current browser session (tab lifecycle).
 *
 * Legacy compatibility:
 * - Reads one-time from old IndexedDB/localStorage keys when session state is empty.
 * - Migrates recovered value into sessionStorage and clears legacy copies.
 */
const memoryStorage = new Map<string, string>();

function getSessionStorage(): Storage | null {
    if (typeof window === 'undefined') return null;
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

function getLocalStorage(): Storage | null {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

async function migrateLegacyState(name: string): Promise<string | null> {
    // Legacy IndexedDB value
    try {
        const legacyValue = await getLegacy(name);
        if (typeof legacyValue === 'string' && legacyValue.length > 0) {
            getSessionStorage()?.setItem(name, legacyValue);
            await delLegacy(name).catch(() => undefined);
            log.info('migrate', 'Migrated from IndexedDB to session storage', { name });
            return legacyValue;
        }
    } catch (error) {
        log.warn('migrate', 'Legacy IndexedDB migration failed', { name, error: error instanceof Error ? error.message : String(error) });
    }

    // Legacy localStorage value (older builds)
    try {
        const local = getLocalStorage();
        const localValue = local?.getItem(name) || null;
        if (localValue) {
            getSessionStorage()?.setItem(name, localValue);
            local?.removeItem(name);
            log.info('migrate', 'Migrated from localStorage to session storage', { name });
            return localValue;
        }
    } catch (error) {
        log.warn('migrate', 'Legacy localStorage migration failed', { name, error: error instanceof Error ? error.message : String(error) });
    }

    return null;
}

export const sessionStateStorage: StateStorage = {
    getItem: async (name: string): Promise<string | null> => {
        try {
            const session = getSessionStorage();
            if (session) {
                const sessionValue = session.getItem(name);
                if (sessionValue !== null) return sessionValue;
                return await migrateLegacyState(name);
            }

            return memoryStorage.get(name) || null;
        } catch (error) {
            log.error('getItem', 'Failed to get item from session storage', { name, error: error instanceof Error ? error.message : String(error) });
            return null;
        }
    },
    setItem: async (name: string, value: string): Promise<void> => {
        try {
            const session = getSessionStorage();
            if (session) {
                session.setItem(name, value);
                return;
            }
            memoryStorage.set(name, value);
        } catch (error) {
            log.error('setItem', 'Failed to set item in session storage', { name, error: error instanceof Error ? error.message : String(error) });
        }
    },
    removeItem: async (name: string): Promise<void> => {
        try {
            getSessionStorage()?.removeItem(name);
            getLocalStorage()?.removeItem(name);
            memoryStorage.delete(name);
            await delLegacy(name).catch(() => undefined);
        } catch (error) {
            log.error('removeItem', 'Failed to remove item from session storage', { name, error: error instanceof Error ? error.message : String(error) });
        }
    },
};
